import { useState, useEffect, useCallback } from 'react';
import { campaignsAPI, advertisersAPI, tenantsAPI, storesAPI } from '../services/api';
import {
  Megaphone, Plus, Play, Pause, CheckCircle, XCircle, Eye, ChevronRight,
  TrendingUp, Users, DollarSign, BarChart2, X, Target, Clock, Globe,
  AlertCircle, RefreshCw, Building2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import TabBar from '../components/TabBar';
import { API_BASE } from '../services/apiBase';

const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';
const API = API_BASE;

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS = {
  draft:            { label: 'Draft',     color: '#6b7280' },
  pending_approval: { label: 'Pending',   color: '#f59e0b' },
  approved:         { label: 'Approved',  color: '#8b5cf6' },
  active:           { label: 'Active',    color: '#22c55e' },
  paused:           { label: 'Paused',    color: '#f59e0b' },
  completed:        { label: 'Done',      color: '#6b7280' },
  rejected:         { label: 'Rejected',  color: '#ef4444' },
  cancelled:        { label: 'Cancelled', color: '#ef4444' },
};

const PRICING = {
  cpm:            'CPM',
  flat_rate:      'Flat',
  per_store_day:  '$/Store/Day',
  fixed_package:  'Package',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(n, dec = 0) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-GB', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtCurrency(micros, currency = 'GBP') {
  if (micros == null) return '—';
  const sym = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '€';
  return sym + fmt(micros / 1_000_000, 2);
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
}
function daysLeft(ends_at) {
  if (!ends_at) return null;
  const diff = Math.ceil((new Date(ends_at) - Date.now()) / 86400000);
  return diff;
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS[status] || { label: status, color: '#6b7280' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: cfg.color + '22', color: cfg.color, letterSpacing: '0.04em',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color,
        boxShadow: status === 'active' ? `0 0 6px ${cfg.color}` : 'none' }} />
      {cfg.label.toUpperCase()}
    </span>
  );
}

// ─── Mini progress bar ────────────────────────────────────────────────────────
function MiniBar({ pct, color = '#2f81f7' }) {
  const p = Math.min(100, Math.max(0, pct || 0));
  const c = p >= 90 ? '#ef4444' : p >= 70 ? '#f59e0b' : color;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 4, background: '#ffffff14', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: c, borderRadius: 2, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: 11, color: c, fontWeight: 700, minWidth: 34 }}>{fmt(p)}%</span>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, accent = '#2f81f7' }) {
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '20px 24px',
      display: 'flex', gap: 16, alignItems: 'flex-start',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: accent + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={18} color={accent} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: accent, marginTop: 4 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ─── Campaign modal (create) ──────────────────────────────────────────────────
function CampaignModal({ advertisers, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', advertiser_id: '', tenant_id: TENANT,
    pricing_model: 'cpm', slot_owner: 'client',
    starts_at: '', ends_at: '',
    cpm_rate: '', budget_total: '', budget_daily: '',
    max_plays_per_store_per_day: '', max_plays_per_store_per_hour: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await campaignsAPI.create({
        ...form,
        cpm_rate:                    form.cpm_rate ? parseFloat(form.cpm_rate) : undefined,
        budget_total:                form.budget_total ? parseFloat(form.budget_total) : undefined,
        budget_daily:                form.budget_daily ? parseFloat(form.budget_daily) : undefined,
        max_plays_per_store_per_day: form.max_plays_per_store_per_day ? parseInt(form.max_plays_per_store_per_day) : undefined,
        max_plays_per_store_per_hour: form.max_plays_per_store_per_hour ? parseInt(form.max_plays_per_store_per_hour) : undefined,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at:   new Date(form.ends_at).toISOString(),
      });
      toast.success('Campaign created');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">New Campaign</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div className="form-group">
            <label className="form-label">Campaign Name</label>
            <input className="form-input" value={form.name} required onChange={e => set('name', e.target.value)} placeholder="e.g. Summer Sale 2026" />
          </div>

          <div className="form-group">
            <label className="form-label">Advertiser</label>
            <select className="form-select" value={form.advertiser_id} required onChange={e => set('advertiser_id', e.target.value)}>
              <option value="">Select advertiser…</option>
              {advertisers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input className="form-input" type="datetime-local" required value={form.starts_at} onChange={e => set('starts_at', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input className="form-input" type="datetime-local" required value={form.ends_at} onChange={e => set('ends_at', e.target.value)} />
            </div>
          </div>

          {/* Pricing */}
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Pricing</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Model</label>
                <select className="form-select" value={form.pricing_model} onChange={e => set('pricing_model', e.target.value)}>
                  <option value="cpm">CPM (per 1000 plays)</option>
                  <option value="flat_rate">Flat Rate</option>
                  <option value="per_store_day">Per Store/Day</option>
                  <option value="fixed_package">Fixed Package</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{form.pricing_model === 'cpm' ? 'CPM Rate (£)' : 'Rate (£)'}</label>
                <input className="form-input" type="number" step="0.01" value={form.cpm_rate} onChange={e => set('cpm_rate', e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Total Budget (£)</label>
                <input className="form-input" type="number" step="0.01" value={form.budget_total} onChange={e => set('budget_total', e.target.value)} placeholder="No cap" />
              </div>
              <div className="form-group">
                <label className="form-label">Daily Budget (£)</label>
                <input className="form-input" type="number" step="0.01" value={form.budget_daily} onChange={e => set('budget_daily', e.target.value)} placeholder="No cap" />
              </div>
            </div>
          </div>

          {/* Frequency caps */}
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Frequency Caps</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Max Plays / Store / Day</label>
                <input className="form-input" type="number" value={form.max_plays_per_store_per_day} onChange={e => set('max_plays_per_store_per_day', e.target.value)} placeholder="Unlimited" />
              </div>
              <div className="form-group">
                <label className="form-label">Max Plays / Store / Hour</label>
                <input className="form-input" type="number" value={form.max_plays_per_store_per_hour} onChange={e => set('max_plays_per_store_per_hour', e.target.value)} placeholder="Unlimited" />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Advertiser modal ─────────────────────────────────────────────────────────
function AdvertiserModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', contact_email: '', contact_name: '', tenant_id: TENANT });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await advertisersAPI.create(form);
      toast.success('Advertiser created');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">New Advertiser</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Company Name</label>
            <input className="form-input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Acme Corp" />
          </div>
          <div className="form-group">
            <label className="form-label">Contact Name</label>
            <input className="form-input" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Jane Smith" />
          </div>
          <div className="form-group">
            <label className="form-label">Contact Email</label>
            <input className="form-input" type="email" required value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="jane@acme.com" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving…' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Campaign detail panel ────────────────────────────────────────────────────
function DetailPanel({ campaign, summary, stores, onClose, onAction }) {
  const days = daysLeft(campaign.ends_at);
  const budgetPct = summary?.budget_utilisation_pct;
  const t = campaign.targeting;

  const statusActions = {
    draft:            [{ label: 'Submit for Review', action: 'submit', icon: ChevronRight, accent: '#8b5cf6' }],
    pending_approval: [{ label: 'Approve', action: 'launch', icon: CheckCircle, accent: '#22c55e' },
                       { label: 'Reject',  action: 'cancel', icon: XCircle,     accent: '#ef4444' }],
    approved:         [{ label: 'Launch',  action: 'launch', icon: Play,         accent: '#22c55e' }],
    active:           [{ label: 'Pause',   action: 'pause',  icon: Pause,        accent: '#f59e0b' }],
    paused:           [{ label: 'Resume',  action: 'launch', icon: Play,         accent: '#22c55e' }],
  };
  const actions = statusActions[campaign.status] || [];

  const targetStoreNames = t?.store_ids?.length
    ? t.store_ids.map(id => stores.find(s => s.id === id)?.name || id.slice(0, 8))
    : null;

  return (
    <div style={{
      width: 360, flexShrink: 0, background: 'var(--bg-surface)',
      borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '18px 20px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>{campaign.name}</div>
          <StatusBadge status={campaign.status} />
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, marginLeft: 8 }}>
          <X size={16} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Actions */}
        {actions.length > 0 && (
          <div style={{ display: 'flex', gap: 8 }}>
            {actions.map(a => (
              <button key={a.action} onClick={() => onAction(campaign.id, a.action)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: `1px solid ${a.accent}44`,
                  background: a.accent + '18', color: a.accent, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                }}>
                <a.icon size={13} /> {a.label}
              </button>
            ))}
          </div>
        )}

        {/* Delivery stats */}
        {summary && (
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Delivery</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { label: 'Total Plays', value: fmt(summary.total_plays) },
                { label: 'Unique Stores', value: fmt(summary.unique_stores) },
                { label: 'Total Spend', value: fmtCurrency(summary.total_cost_micros, summary.currency) },
                { label: 'Plays Left', value: summary.plays_remaining != null ? fmt(summary.plays_remaining) : '∞' },
              ].map(m => (
                <div key={m.label}>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{m.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.label}</div>
                </div>
              ))}
            </div>
            {budgetPct != null && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Budget Used</div>
                <MiniBar pct={budgetPct} />
              </div>
            )}
          </div>
        )}

        {/* Schedule */}
        <Section icon={Clock} label="Schedule">
          <Row label="Starts" value={fmtDate(campaign.starts_at)} />
          <Row label="Ends" value={fmtDate(campaign.ends_at)} />
          {days != null && <Row label="Days Remaining" value={days > 0 ? `${days}d` : days === 0 ? 'Today' : 'Ended'} accent={days < 3 ? '#ef4444' : days < 7 ? '#f59e0b' : null} />}
        </Section>

        {/* Pricing */}
        <Section icon={DollarSign} label="Pricing">
          <Row label="Model" value={PRICING[campaign.pricing_model] || campaign.pricing_model} />
          {campaign.cpm_rate != null && <Row label="CPM Rate" value={`£${fmt(campaign.cpm_rate, 2)}`} />}
          {campaign.budget_total != null && <Row label="Total Budget" value={`£${fmt(campaign.budget_total, 2)}`} />}
          {campaign.budget_daily != null && <Row label="Daily Budget" value={`£${fmt(campaign.budget_daily, 2)}`} />}
        </Section>

        {/* Frequency caps */}
        <Section icon={AlertCircle} label="Frequency Caps">
          <Row label="Max / Store / Day" value={campaign.max_plays_per_store_per_day ?? '∞'} />
          <Row label="Max / Store / Hour" value={campaign.max_plays_per_store_per_hour ?? '∞'} />
          {campaign.max_total_plays != null && <Row label="Max Total" value={fmt(campaign.max_total_plays)} />}
        </Section>

        {/* Targeting */}
        <Section icon={Target} label="Store Targeting">
          {targetStoreNames ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {targetStoreNames.map((s, i) => (
                <span key={i} style={{ padding: '2px 8px', borderRadius: 999, background: '#2f81f722', color: '#2f81f7', fontSize: 11, fontWeight: 600 }}>{s}</span>
              ))}
            </div>
          ) : (
            <Row label="Stores" value="All stores" />
          )}
          {t?.days_of_week?.length > 0 && <Row label="Days" value={t.days_of_week.map(d => d.slice(0,3).toUpperCase()).join(' · ')} />}
          {t?.time_start && <Row label="Time Window" value={`${t.time_start} – ${t.time_end}`} />}
        </Section>

      </div>
    </div>
  );
}

function Section({ icon: Icon, label, children }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Icon size={13} color="var(--text-muted)" />
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: accent || 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function Campaigns() {
  const [tab, setTab] = useState('campaigns'); // campaigns | advertisers
  const [campaigns, setCampaigns] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [stores, setStores] = useState([]);
  const [summaries, setSummaries] = useState({}); // campaign_id -> summary
  const [selected, setSelected] = useState(null); // campaign object
  const [loading, setLoading] = useState(true);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showAdvertiserModal, setShowAdvertiserModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, aRes, sRes] = await Promise.all([
        campaignsAPI.list({ tenant_id: TENANT }),
        advertisersAPI.list({ tenant_id: TENANT }),
        storesAPI.list({ tenant_id: TENANT }),
      ]);
      const cList = cRes.data || [];
      setCampaigns(cList);
      setAdvertisers(aRes.data || []);
      setStores(sRes.data || []);

      // Fetch summaries for active/paused campaigns
      const live = cList.filter(c => ['active', 'paused', 'completed'].includes(c.status));
      const sumResults = await Promise.allSettled(live.map(c => campaignsAPI.summary(c.id)));
      const map = {};
      sumResults.forEach((r, i) => {
        if (r.status === 'fulfilled') map[live[i].id] = r.value.data;
      });
      setSummaries(map);
    } catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    try {
      if (action === 'submit') await campaignsAPI.submit(id);
      else if (action === 'launch') await campaignsAPI.launch(id);
      else if (action === 'pause') await campaignsAPI.pause(id);
      else if (action === 'cancel') {
        if (!window.confirm('Cancel this campaign?')) return;
        // no direct API call mapped — just refresh
      }
      toast.success('Campaign updated');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const advertiserMap = Object.fromEntries(advertisers.map(a => [a.id, a]));

  // KPIs
  const active   = campaigns.filter(c => c.status === 'active').length;
  const totalPlays = Object.values(summaries).reduce((s, v) => s + (v?.total_plays || 0), 0);
  const totalSpend = Object.values(summaries).reduce((s, v) => s + (v?.total_cost_micros || 0), 0);

  // Filter
  let filtered = campaigns;
  if (statusFilter !== 'all') filtered = filtered.filter(c => c.status === statusFilter);
  if (search) filtered = filtered.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (advertiserMap[c.advertiser_id]?.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Ads Manager</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>Campaigns, advertisers and delivery tracking</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
          {tab === 'advertisers'
            ? <button className="btn btn-primary" onClick={() => setShowAdvertiserModal(true)}><Plus size={14} /> New Advertiser</button>
            : <button className="btn btn-primary" onClick={() => setShowCampaignModal(true)}><Plus size={14} /> New Campaign</button>
          }
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <KpiCard icon={Megaphone}   label="Total Campaigns" value={campaigns.length}     sub={`${active} active`}  accent="#2f81f7" />
        <KpiCard icon={Play}        label="Active Now"       value={active}               sub="currently live"      accent="#22c55e" />
        <KpiCard icon={TrendingUp}  label="Total Plays"      value={fmt(totalPlays)}      sub="across all campaigns" accent="#8b5cf6" />
        <KpiCard icon={DollarSign}  label="Total Spend"      value={fmtCurrency(totalSpend)} sub="all time"         accent="#f59e0b" />
      </div>

      <TabBar
        tabs={[
          { id: 'campaigns', label: 'Campaigns', count: campaigns.length },
          { id: 'advertisers', label: 'Advertisers', count: advertisers.length },
        ]}
        active={tab}
        onChange={setTab}
      />

      {/* Content area */}
      <div style={{ display: 'flex', gap: 0, flex: 1, overflow: 'hidden', minHeight: 0, borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg-surface)' }}>

        {/* Main panel */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {tab === 'campaigns' && (
            <>
              {/* Filters bar */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', position: 'sticky', top: 0, background: 'var(--bg-surface)', zIndex: 1 }}>
                <input
                  className="form-input" style={{ width: 220, height: 34 }}
                  placeholder="Search campaigns or advertisers…"
                  value={search} onChange={e => setSearch(e.target.value)}
                />
                <select className="form-select" style={{ width: 140, height: 34 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="all">All statuses</option>
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} campaigns</div>
              </div>

              {/* Table */}
              {loading ? (
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
                  <Megaphone size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
                  <div>No campaigns found</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Campaign', 'Advertiser', 'Status', 'Period', 'Pricing', 'Freq. Cap', 'Plays', 'Budget Used', ''].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(c => {
                      const adv = advertiserMap[c.advertiser_id];
                      const sum = summaries[c.id];
                      const isSelected = selected?.id === c.id;
                      const days = daysLeft(c.ends_at);

                      return (
                        <tr key={c.id}
                          onClick={() => setSelected(isSelected ? null : c)}
                          style={{
                            borderBottom: '1px solid var(--border)',
                            background: isSelected ? 'var(--bg-hover)' : 'transparent',
                            cursor: 'pointer', transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                        >
                          {/* Campaign name */}
                          <td style={{ padding: '12px 16px', maxWidth: 200 }}>
                            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                            {c.targeting?.store_ids?.length > 0 && (
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Target size={10} /> {c.targeting.store_ids.length} stores
                              </div>
                            )}
                          </td>

                          {/* Advertiser */}
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{adv?.name || '—'}</div>
                          </td>

                          {/* Status */}
                          <td style={{ padding: '12px 16px' }}>
                            <StatusBadge status={c.status} />
                          </td>

                          {/* Period */}
                          <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: 12 }}>{fmtDate(c.starts_at)} – {fmtDate(c.ends_at)}</div>
                            {days != null && c.status === 'active' && (
                              <div style={{ fontSize: 11, color: days < 3 ? '#ef4444' : days < 7 ? '#f59e0b' : 'var(--text-muted)', marginTop: 2 }}>
                                {days > 0 ? `${days}d left` : 'Ends today'}
                              </div>
                            )}
                          </td>

                          {/* Pricing */}
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: '#2f81f718', color: '#2f81f7', fontWeight: 600 }}>
                              {PRICING[c.pricing_model] || c.pricing_model}
                            </span>
                          </td>

                          {/* Frequency cap */}
                          <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                            {c.max_plays_per_store_per_day != null ? (
                              <div style={{ fontSize: 12 }}>
                                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.max_plays_per_store_per_day}</span>
                                <span style={{ color: 'var(--text-muted)' }}>/day</span>
                                {c.max_plays_per_store_per_hour != null && (
                                  <span style={{ color: 'var(--text-muted)' }}> · {c.max_plays_per_store_per_hour}/hr</span>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>∞</span>
                            )}
                          </td>

                          {/* Plays */}
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ fontSize: 13, fontWeight: 700 }}>{sum ? fmt(sum.total_plays) : '—'}</span>
                          </td>

                          {/* Budget used */}
                          <td style={{ padding: '12px 16px', minWidth: 120 }}>
                            {sum?.budget_utilisation_pct != null
                              ? <MiniBar pct={sum.budget_utilisation_pct} />
                              : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
                            }
                          </td>

                          {/* Expand arrow */}
                          <td style={{ padding: '12px 12px' }}>
                            <ChevronRight size={14} color="var(--text-muted)"
                              style={{ transform: isSelected ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}

          {tab === 'advertisers' && (
            <>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', position: 'sticky', top: 0, background: 'var(--bg-surface)', zIndex: 1 }}>
                {advertisers.length} advertisers
              </div>
              {loading ? (
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Advertiser', 'Contact', 'Status', 'Campaigns', 'Billing Ref'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {advertisers.map(a => {
                      const campCount = campaigns.filter(c => c.advertiser_id === a.id).length;
                      const activeCount = campaigns.filter(c => c.advertiser_id === a.id && c.status === 'active').length;
                      return (
                        <tr key={a.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#2f81f722', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Building2 size={14} color="#2f81f7" />
                              </div>
                              <div>
                                <div style={{ fontWeight: 600 }}>{a.name}</div>
                                {a.is_platform_owned && <div style={{ fontSize: 11, color: '#8b5cf6' }}>Platform Account</div>}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: 12 }}>{a.contact_name || '—'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.contact_email}</div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              fontSize: 11, padding: '3px 9px', borderRadius: 999, fontWeight: 700,
                              background: a.status === 'active' ? '#22c55e22' : '#6b728022',
                              color: a.status === 'active' ? '#22c55e' : '#6b7280',
                            }}>{(a.status || 'pending').toUpperCase()}</span>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ fontWeight: 700 }}>{campCount}</span>
                            {activeCount > 0 && <span style={{ fontSize: 11, color: '#22c55e', marginLeft: 6 }}>{activeCount} live</span>}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{a.billing_reference || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {/* Detail side panel */}
        {selected && tab === 'campaigns' && (
          <DetailPanel
            campaign={selected}
            summary={summaries[selected.id] || null}
            stores={stores}
            onClose={() => setSelected(null)}
            onAction={handleAction}
          />
        )}
      </div>

      {/* Modals */}
      {showCampaignModal && (
        <CampaignModal
          advertisers={advertisers}
          onClose={() => setShowCampaignModal(false)}
          onSaved={() => { setShowCampaignModal(false); load(); }}
        />
      )}
      {showAdvertiserModal && (
        <AdvertiserModal
          onClose={() => setShowAdvertiserModal(false)}
          onSaved={() => { setShowAdvertiserModal(false); load(); }}
        />
      )}
    </div>
  );
}
